// Lib
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDragLayer } from 'react-dnd';

// Utils
import { collector } from './elementDragLayerCollect';
import { clearDragDimension } from '../../../element/dnd/dragAndDropStateSingleton';

// Components
import ElementDragLayerStateManager from './ElementDragLayerStateManager';

// Selectors
import { getGridSize } from '../../../utils/grid/gridSizeSelector';
import { getDragModifierKeys } from '../../../utils/dnd/modifierKeys/dragModifierKeysSelector';
import { canvasZoomScaleSelector } from '../../../canvas/store/canvasZoomSelector';
import { areDnd3dEffectsEnabledSelector } from '../../../user/account/accountModal/interface/interfaceSettingsSelector';
import { attachModeHoveredElementIdSelector, getIsReplaceModeActive } from '../../../reducers/draggingSelector';

// Constants
import { debugToolProperties } from '../../../debug/store/debugToolSelector';
import { DEBUG_TOOLS } from '../../../debug/store/debugToolConstants';
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

const DragLayerComponent = (props: any) => {
    const dragProps = useDragLayer((monitor) => collector(monitor, props));
    return <ElementDragLayerStateManager {...props} {...dragProps} usingHookDragLayer />;
};

type ElementDragLayerProps = {
    currentBoardId: string;
    permissions: number;
};

const ElementDragLayer = ({ currentBoardId, permissions }: ElementDragLayerProps) => {
    // helps avoid a scale bug on the first frame of dragging
    // (see getZoomScaleOnHoveredTarget in elementDragLayerCollect.js for more info)
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);

        return () => {
            clearDragDimension();
        };
    }, []);

    // used directly by the drag layer
    const isReplaceModeActive = useSelector(getIsReplaceModeActive);
    const gridSize = useSelector(getGridSize);
    const zoomScale = useSelector(canvasZoomScaleSelector);
    const dragModifierKeys = useSelector(getDragModifierKeys);
    const platformDetails = useSelector(getPlatformDetailsSelector);

    // passed through to elementDragLayerStateManager (it can't use hooks as it's a class)
    const areDnd3dEffectsEnabled = useSelector(areDnd3dEffectsEnabledSelector);
    const attachModeHoveredId = useSelector(attachModeHoveredElementIdSelector);

    // Debugging DND
    const dndDebugToolProperties = useSelector(debugToolProperties(DEBUG_TOOLS.DND));

    return (
        <DragLayerComponent
            currentBoardId={currentBoardId}
            permissions={permissions}
            mounted={mounted}
            gridSize={gridSize}
            zoomScale={zoomScale}
            isReplaceModeActive={isReplaceModeActive}
            dragModifierKeys={dragModifierKeys}
            areDnd3dEffectsEnabled={areDnd3dEffectsEnabled}
            attachModeHoveredId={attachModeHoveredId}
            dndDebugToolProperties={dndDebugToolProperties}
            platformDetails={platformDetails}
        />
    );
};

export default ElementDragLayer;
